import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  buildStripeDefaultBilling,
  getStripeBillingPrefillKey,
  getStripeElementsOptions,
  getStripePaymentElementOptions,
  STRIPE_CHECKOUT_PAYMENT_ELEMENT_CONTAINER_CLASS,
  cacheStripeKeyResponse,
} from "../../utils/stripePaymentUi";
import StripeExpressCheckout from "../Payment/StripeExpressCheckout";
import { useDispatch, useSelector } from "react-redux";
import { getAllOrdersOfShop } from "../../redux/actions/order";
import { loadUser } from "../../redux/actions/user";
import styles from "../../styles/styles";
import { RxCross1 } from "react-icons/rx";
import axios from "axios";
import { server } from "../../server";
import { toast } from "react-toastify";
import {
  PaymentElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { normalizeDecimalTyping } from "../../utils/decimalInput";
import { exceedsBalance, maxWithdrawableAmount, roundMoneyMajor } from "../../utils/money";

const formatCurrency = (value, currency = "GBP") => {
  if (value === null || value === undefined) return "£0.00";
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: currency || "GBP",
  }).format(Number(value));
};

const WALLET_TOP_UP_MIN = 10;
const WALLET_TOP_UP_MAX = 10000;

const WalletTopUpFormWrapper = ({ currency, onSuccess, stripePromise }) => {
  const { user } = useSelector((state) => state.user);
  const billingDefaults = useMemo(() => buildStripeDefaultBilling(null, user), [user]);
  const billingPrefillKey = getStripeBillingPrefillKey(billingDefaults);
  const [amount, setAmount] = useState("50");
  const [confirmedAmount, setConfirmedAmount] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentMethodConfigurationId, setPaymentMethodConfigurationId] = useState("");
  const [isCreatingIntent, setIsCreatingIntent] = useState(false);

  const resetPaymentStep = useCallback((resetAmount = false) => {
    setClientSecret(null);
    setPaymentMethodConfigurationId("");
    setConfirmedAmount(null);
    if (resetAmount) setAmount("50");
  }, []);

  const handleContinue = async (e) => {
    e.preventDefault();
    const numericAmount = Number(amount);
    if (!numericAmount || numericAmount <= 0) {
      toast.error("Enter a valid amount to add.");
      return;
    }
    if (numericAmount < WALLET_TOP_UP_MIN) {
      toast.error(`Minimum top-up amount is ${formatCurrency(WALLET_TOP_UP_MIN, currency)}.`);
      return;
    }
    if (numericAmount > WALLET_TOP_UP_MAX) {
      toast.error(`Top-up amount exceeds the maximum limit (${formatCurrency(WALLET_TOP_UP_MAX, currency)}).`);
      return;
    }

    setIsCreatingIntent(true);
    try {
      const buyerName = [user?.firstName, user?.lastName]
        .filter(Boolean)
        .join(" ")
        .trim() || String(user?.name || user?.username || "").trim();
      const buyerEmail = String(user?.email || "").trim();
      const { data } = await axios.post(
        `${server}/payment/stripe/intent`,
        {
          amount: numericAmount,
          currency,
          metadata: { context: "wallet-topup" },
          ...(buyerEmail ? { buyerEmail, buyerName: buyerName || undefined } : {}),
        },
        { withCredentials: true }
      );
      if (data?.clientSecret) {
        setConfirmedAmount(numericAmount);
        setClientSecret(data.clientSecret);
        setPaymentMethodConfigurationId(data?.paymentMethodConfigurationId || "");
      } else {
        toast.error("Unable to start payment. Please try again.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Unable to start payment.");
    } finally {
      setIsCreatingIntent(false);
    }
  };

  if (!clientSecret || confirmedAmount == null) {
    return (
      <form onSubmit={handleContinue} className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-slate-600">Amount to add</label>
          <div className="mt-1 flex items-center gap-2">
            <input
              type="number"
              min={WALLET_TOP_UP_MIN}
              max={WALLET_TOP_UP_MAX}
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(normalizeDecimalTyping(e.target.value))}
              className="w-full rounded border border-slate-300 px-3 py-2 focus:border-[#38513B] focus:outline-none focus:ring-0"
              placeholder="50.00"
            />
            <span className="text-sm font-semibold text-slate-500">{currency}</span>
          </div>
          <p className="mt-1 text-xs text-slate-500">
            Min {formatCurrency(WALLET_TOP_UP_MIN, currency)} · max {formatCurrency(WALLET_TOP_UP_MAX, currency)}
          </p>
        </div>
        <button
          type="submit"
          disabled={isCreatingIntent}
          className={`${styles.button} !w-full !justify-center text-white disabled:opacity-60`}
        >
          {isCreatingIntent ? "Preparing payment…" : "Continue to payment"}
        </button>
      </form>
    );
  }

  return (
    <Elements
      key={`${clientSecret}__${billingPrefillKey}__${paymentMethodConfigurationId}`}
      stripe={stripePromise}
      options={getStripeElementsOptions({
        clientSecret,
        billingDefaults,
        paymentMethodConfiguration: paymentMethodConfigurationId,
      })}
    >
      <WalletTopUpForm
        currency={currency}
        amount={confirmedAmount}
        clientSecret={clientSecret}
        onSuccess={() => {
          resetPaymentStep(true);
          onSuccess?.();
        }}
        onChangeAmount={() => resetPaymentStep(false)}
        defaultBillingDetails={billingDefaults}
      />
    </Elements>
  );
};

const WalletTopUpForm = ({
  currency,
  amount,
  clientSecret,
  onSuccess,
  onChangeAmount,
  defaultBillingDetails,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const stripe = useStripe();
  const elements = useElements();

  const runTopUp = async () => {
    const numericAmount = Number(amount);
    if (!numericAmount || numericAmount <= 0) {
      toast.error("Enter a valid amount to add.");
      return;
    }
    if (!stripe || !elements || !clientSecret) {
      toast.error("Payment system is not ready. Please wait and try again.");
      return;
    }
    try {
      setIsProcessing(true);
      const { error: submitError } = await elements.submit();
      if (submitError) throw new Error(submitError.message || "Check your payment details.");
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        clientSecret,
        redirect: "if_required",
      });
      if (error) throw new Error(error.message || "Payment failed.");
      if (paymentIntent.status !== "succeeded") throw new Error("Payment not completed.");
      await axios.post(
        `${server}/shop/top-up-wallet`,
        { amount: numericAmount, currency, paymentIntentId: paymentIntent.id, paymentMethod: "stripe" },
        { withCredentials: true }
      );
      toast.success("Wallet topped up successfully!");
      onSuccess?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Payment failed.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleTopUp = async (e) => {
    e.preventDefault();
    await runTopUp();
  };

  return (
    <form onSubmit={handleTopUp} className="space-y-3">
      <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
        <div>
          <p className="text-xs font-medium text-slate-500">Amount to add</p>
          <p className="text-lg font-semibold text-slate-900">{formatCurrency(amount, currency)}</p>
        </div>
        <button
          type="button"
          onClick={onChangeAmount}
          className="text-xs font-semibold text-[#38513B] hover:underline"
        >
          Change amount
        </button>
      </div>
      <div className="space-y-2">
        <p className="text-sm text-slate-600">
          Complete your payment securely with your card.
        </p>
        <StripeExpressCheckout onConfirm={runTopUp} className="mb-2" />
        <div className={STRIPE_CHECKOUT_PAYMENT_ELEMENT_CONTAINER_CLASS}>
          <PaymentElement options={getStripePaymentElementOptions(defaultBillingDetails)} />
        </div>
      </div>
      <button
        type="submit"
        disabled={isProcessing}
        className={`${styles.button} !w-full !justify-center text-white disabled:opacity-60`}
      >
        {isProcessing ? "Processing..." : "Add funds"}
      </button>
    </form>
  );
};

const Wallet = () => {
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState("50");
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const withdrawInFlightRef = useRef(false);
  const [stripePromise, setStripePromise] = useState(null);
  const [stripeReady, setStripeReady] = useState(false);
  const [walletSummary, setWalletSummary] = useState({
    balance: 0,
    currency: "GBP",
    transactions: [],
    outstandingEscrow: [],
  });
  const [withdrawSummary, setWithdrawSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(true);

  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.user);
  const prevUserIdRef = useRef(null);
  const isFetchingRef = useRef(false);

  const isSeller = Boolean(user?.isSeller);
  const walletBalance = roundMoneyMajor(walletSummary.balance ?? user?.availableBalance ?? 0);
  const withdrawSummaryReady = Boolean(withdrawSummary?.success);
  const withdrawableBalance = roundMoneyMajor(
    withdrawSummaryReady ? withdrawSummary.withdrawableBalance : 0
  );
  const pendingSettlement = roundMoneyMajor(withdrawSummary?.pendingSettlement ?? 0);
  const pendingEscrowAmount = useMemo(
    () =>
      roundMoneyMajor(
        (walletSummary.outstandingEscrow || []).reduce((sum, row) => {
          if (row?.awaitingBuyerConfirmation === false) return sum;
          return sum + Number(row?.amount || 0);
        }, 0)
      ),
    [walletSummary.outstandingEscrow]
  );
  const pendingEscrowOrderCount = useMemo(
    () =>
      (walletSummary.outstandingEscrow || []).filter(
        (row) => row?.awaitingBuyerConfirmation !== false
      ).length,
    [walletSummary.outstandingEscrow]
  );
  const awaitingReleaseAmount = useMemo(
    () =>
      roundMoneyMajor(
        (walletSummary.outstandingEscrow || []).reduce((sum, row) => {
          if (row?.awaitingBuyerConfirmation !== false) return sum;
          return sum + Number(row?.amount || 0);
        }, 0)
      ),
    [walletSummary.outstandingEscrow]
  );
  const currency = walletSummary.currency || user?.walletCurrency || "GBP";
  const totalPendingAmount = useMemo(
    () => roundMoneyMajor(pendingEscrowAmount + awaitingReleaseAmount + pendingSettlement),
    [pendingEscrowAmount, awaitingReleaseAmount, pendingSettlement]
  );
  const pendingEscrowOrdersLink = "/profile/orders?view=selling&awaitingConfirmation=1";
  const pendingStatusLines = useMemo(() => {
    const lines = [];
    if (awaitingReleaseAmount > 0.01) {
      lines.push(
        `${formatCurrency(awaitingReleaseAmount, currency)} confirmed and awaiting release to your wallet.`
      );
    }
    if (pendingSettlement > 0.01) {
      lines.push(
        `${formatCurrency(pendingSettlement, currency)} from recent card sales settling (usually 1–2 business days).`
      );
    }
    if (lines.length === 0 && pendingEscrowOrderCount === 0) {
      lines.push("No sales or settlements pending.");
    }
    return lines;
  }, [awaitingReleaseAmount, pendingSettlement, currency, pendingEscrowOrderCount]);
  const maxWithdraw = maxWithdrawableAmount(withdrawableBalance);
  const hasStripeConnect = Boolean(user?.stripeConnectAccountId);

  const loadWalletSummary = useCallback(async () => {
    if (!user?._id || !user?.isSeller) {
      setLoadingSummary(false);
      return;
    }
    try {
      const [walletRes, withdrawRes] = await Promise.all([
        axios.get(`${server}/wallet/me`, { withCredentials: true }),
        axios.get(`${server}/withdraw/summary`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      const data = walletRes.data;
      setWalletSummary({
        balance: data.balance ?? 0,
        currency: data.currency || "GBP",
        transactions: data.transactions || [],
        outstandingEscrow: data.outstandingEscrow || [],
      });
      if (withdrawRes?.data?.success) {
        setWithdrawSummary(withdrawRes.data);
      } else {
        setWithdrawSummary(null);
      }
      dispatch(loadUser());
    } catch {
      setWalletSummary((prev) => ({ ...prev, transactions: [], outstandingEscrow: [] }));
      setWithdrawSummary(null);
    } finally {
      setLoadingSummary(false);
    }
  }, [user?._id, user?.isSeller, dispatch]);

  useEffect(() => {
    if (user?.isSeller && user?._id) loadWalletSummary();
    else setLoadingSummary(false);
  }, [user?._id, user?.isSeller, loadWalletSummary, dispatch]);

  useEffect(() => {
    if (!user?._id || !user?.isSeller || user._id === prevUserIdRef.current || isFetchingRef.current) return;
    prevUserIdRef.current = user._id;
    isFetchingRef.current = true;
    dispatch(getAllOrdersOfShop(user._id)).finally(() => { isFetchingRef.current = false; });
  }, [dispatch, user?._id, user?.isSeller]);

  useEffect(() => {
    const fetchStripeKey = async () => {
      try {
        const { data } = await axios.get(`${server}/payment/stripe/key`, { withCredentials: true });
        cacheStripeKeyResponse(data);
        setStripePromise(data?.publishableKey ? loadStripe(data.publishableKey.trim()) : null);
      } catch {
        setStripePromise(null);
      } finally {
        setStripeReady(true);
      }
    };
    fetchStripeKey();
  }, []);

  const handleWithdraw = async () => {
    if (withdrawInFlightRef.current || isWithdrawing) return;

    const amt = Number(withdrawAmount);
    if (!Number.isFinite(amt) || amt <= 0) {
      toast.error("Please enter a valid withdrawal amount.");
      return;
    }
    if (exceedsBalance(amt, walletBalance)) {
      toast.error("You can't withdraw more than your wallet balance.");
      return;
    }
    if (!withdrawSummaryReady) {
      toast.error(
        "Withdrawal availability is still loading. Please wait a moment or refresh the page."
      );
      return;
    }
    if (exceedsBalance(amt, withdrawableBalance)) {
      toast.error(
        pendingSettlement > 0
          ? `Only ${formatCurrency(withdrawableBalance, currency)} can be withdrawn now. ${formatCurrency(pendingSettlement, currency)} is still settling from card payments (usually 1–2 business days).`
          : "Withdrawal is temporarily unavailable while card payments finish settling. Please try again in 1–2 business days."
      );
      return;
    }
    if (!hasStripeConnect) {
      toast.error("Please connect your Stripe account before requesting a withdrawal.");
      return;
    }

    withdrawInFlightRef.current = true;
    setIsWithdrawing(true);
    try {
      const { data } = await axios.post(
        `${server}/withdraw/create-withdraw-request`,
        { amount: amt },
        { withCredentials: true }
      );
      toast.success(
        data?.message || "Withdrawal sent to your bank. Transfers usually arrive within 3–7 business days."
      );
      setWithdrawOpen(false);
      await loadWalletSummary();
    } catch (err) {
      const message =
        err?.response?.data?.message || err.message || "Withdraw failed.";
      toast.error(message);
      await loadWalletSummary();
    } finally {
      withdrawInFlightRef.current = false;
      setIsWithdrawing(false);
    }
  };

  const handleConnectStripe = async () => {
    try {
      const { data } = await axios.post(`${server}/shop/stripe/connect-link`, {}, { withCredentials: true });
      if (data?.url) window.location.href = data.url;
      else toast.error("Unable to start Stripe onboarding.");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Unable to connect Stripe. Try again later.");
    }
  };

  const refreshWallet = () => {
    dispatch(loadUser());
    loadWalletSummary();
  };

  const activityRows = useMemo(() => {
    const escrow = (walletSummary.outstandingEscrow || []).map((row, idx) => ({
      rowKey: row.id || `escrow-${idx}`,
      sortAt: row.createdAt ? new Date(row.createdAt).getTime() : 0,
      kind: "escrow",
      row,
    }));
    const txs = (walletSummary.transactions || []).map((tx, idx) => ({
      rowKey: tx.id ? String(tx.id) : `tx-${idx}-${tx.reference || ""}-${tx.createdAt || idx}`,
      sortAt: tx.createdAt ? new Date(tx.createdAt).getTime() : 0,
      kind: "wallet",
      tx,
    }));
    return [...escrow, ...txs].sort((a, b) => b.sortAt - a.sortAt);
  }, [walletSummary.outstandingEscrow, walletSummary.transactions]);

  if (!isSeller) {
    return (
      <div className="w-full min-h-[90vh] space-y-6 p-6">
        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900">Wallet</h2>
          <p className="text-sm text-slate-500 mt-1">
            Your wallet is available when you sell on the platform. You can add funds, withdraw earnings, and see activity here.
          </p>
          <div className="mt-6 p-4 rounded-lg bg-slate-50 border border-slate-200">
            <p className="text-slate-700 font-medium">Current balance</p>
            <p className="text-2xl font-bold text-[#38513B] mt-1">£0.00</p>
            <p className="text-sm text-slate-500 mt-2">
              Start selling to receive payments and use your wallet.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-[90vh] space-y-6 p-6">
      <div id="wallet-activity" className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <header className="mb-6">
          <h2 className="text-lg font-semibold text-slate-900">Wallet</h2>
          <p className="text-sm text-slate-500">
            View your balance, add funds, withdraw to your bank, and see recent activity.
          </p>
        </header>

        {/* Balance */}
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="grid w-full max-w-xl gap-4 sm:grid-cols-2">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4">
              <p className="text-sm font-medium text-slate-600">Available to withdraw</p>
              {loadingSummary ? (
                <p className="mt-2 text-slate-500">Loading…</p>
              ) : (
                <>
                  <p className="mt-1 text-3xl font-bold text-[#38513B]">
                    {formatCurrency(withdrawableBalance, currency)}
                  </p>
                  <p className="mt-2 text-sm text-slate-600">
                    Cleared in your wallet and ready to send to your bank.
                  </p>
                </>
              )}
            </div>

            <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4">
              <p className="text-sm font-medium text-slate-600">Pending</p>
              {loadingSummary ? (
                <p className="mt-2 text-slate-500">Loading…</p>
              ) : (
                <>
                  <p className="mt-1 text-3xl font-bold text-amber-900">
                    {formatCurrency(totalPendingAmount, currency)}
                  </p>
                  <div className="mt-2 space-y-1">
                    {pendingEscrowOrderCount > 0 ? (
                      <p className="text-sm text-slate-600">
                        From{" "}
                        {pendingEscrowOrderCount === 1
                          ? "1 order"
                          : `${pendingEscrowOrderCount} orders`}{" "}
                        awaiting buyer delivery confirmation.{" "}
                        <Link
                          to={pendingEscrowOrdersLink}
                          className="font-semibold text-[#38513b] underline hover:text-[#2f4232]"
                        >
                          View orders
                        </Link>
                      </p>
                    ) : null}
                    {pendingStatusLines.map((line) => (
                      <p key={line} className="text-sm text-slate-600">
                        {line}
                      </p>
                    ))}
                  </div>
                </>
              )}
            </div>

            {!loadingSummary && !hasStripeConnect && (
              <p className="text-sm text-amber-600 sm:col-span-2">
                Use Manage wallet to connect your bank and enable payouts.
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              className="w-[200px] h-[44px] flex items-center justify-center rounded-xl font-semibold text-white bg-[#38513B] !rounded hover:opacity-90"
              onClick={handleConnectStripe}
            >
              Manage wallet
            </button>
            <button
              type="button"
              className={`${styles.button} !h-[44px] !rounded text-white w-[200px]`}
              onClick={() => {
                if (!hasStripeConnect) {
                  toast.error("Complete payout setup in Manage wallet first.");
                  return;
                }
                if (!withdrawSummaryReady) {
                  toast.error(
                    "Withdrawal availability is still loading. Please wait a moment or refresh the page."
                  );
                  return;
                }
                if (maxWithdraw <= 0) toast.error("No balance to withdraw.");
                else {
                  setWithdrawAmount(String(maxWithdraw));
                  setWithdrawOpen(true);
                }
              }}
            >
              Withdraw
            </button>
          </div>
        </div>
      </div>

      {/* Add funds */}
      <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-slate-800">Add funds</h3>
        <p className="mt-1 text-sm text-slate-500">
          Top up your wallet with your card.
        </p>
        {!stripeReady ? (
          <p className="mt-4 text-sm text-slate-500">Checking payment options…</p>
        ) : stripePromise ? (
          <div className="mt-4">
            <WalletTopUpFormWrapper
              stripePromise={stripePromise}
              currency={currency}
              onSuccess={refreshWallet}
            />
          </div>
        ) : (
          <p className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-700">
            Card top-ups are not available right now. Configure Stripe in admin to add funds here.
          </p>
        )}
      </div>

      {/* Outstanding / Activity */}
      <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-slate-800">Activity &amp; outstanding</h3>
        <p className="mt-1 text-sm text-slate-500">
          Wallet movements (top-ups, withdrawals, released payouts) and{" "}
          <strong>outstanding</strong> sales: your share (not VA buyer protection or platform fees)
          until the buyer confirms delivery.
        </p>
        {loadingSummary ? (
          <p className="mt-4 text-sm text-slate-500">Loading activity…</p>
        ) : !activityRows.length ? (
          <p className="mt-4 text-slate-500">No activity yet.</p>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-slate-600">
                  <th className="pb-2 pr-4">Date</th>
                  <th className="pb-2 pr-4">Type</th>
                  <th className="pb-2 pr-4">Description</th>
                  <th className="pb-2 text-right">Amount</th>
                  <th className="pb-2 pl-2 text-right">Balance after</th>
                </tr>
              </thead>
              <tbody>
                {activityRows.map(({ rowKey, kind, tx, row }) =>
                  kind === "escrow" ? (
                    <tr key={rowKey} className="border-b border-slate-100 bg-amber-50/40">
                      <td className="py-2 pr-4 text-slate-600">
                        {row.createdAt ? new Date(row.createdAt).toLocaleString() : "—"}
                      </td>
                      <td className="py-2 pr-4">
                        <span className="font-medium text-amber-800">Outstanding</span>
                      </td>
                      <td className="py-2 pr-4 text-slate-700">
                        <div>{row.notes || "—"}</div>
                        {row.orderTotal != null ? (
                          <div className="text-xs text-slate-500 mt-0.5">
                            Order {formatCurrency(row.orderTotal, row.currency)} · VA fees{" "}
                            {formatCurrency(row.vaFees ?? 0, row.currency)}
                            {row.buyerProtectionFee > 0
                              ? ` (incl. buyer protection ${formatCurrency(row.buyerProtectionFee, row.currency)})`
                              : ""}
                          </div>
                        ) : null}
                      </td>
                      <td className="py-2 text-right font-medium text-amber-900">
                        +{formatCurrency(row.sellerShare ?? row.amount, row.currency)}{" "}
                        <span className="text-xs font-normal text-slate-500">your share</span>
                      </td>
                      <td className="py-2 pl-2 text-right text-slate-500">—</td>
                    </tr>
                  ) : (
                    <tr key={rowKey} className="border-b border-slate-100">
                      <td className="py-2 pr-4 text-slate-600">
                        {tx.createdAt ? new Date(tx.createdAt).toLocaleString() : "—"}
                      </td>
                      <td className="py-2 pr-4">
                        <span
                          className={
                            tx.type === "credit"
                              ? "text-emerald-600 font-medium"
                              : "text-rose-600 font-medium"
                          }
                        >
                          {tx.type === "credit" ? "In" : "Out"}
                        </span>
                      </td>
                      <td className="py-2 pr-4 text-slate-700">{tx.notes || tx.reference || "—"}</td>
                      <td className="py-2 text-right font-medium">
                        {tx.type === "credit" ? "+" : "-"}
                        {formatCurrency(tx.amount, tx.currency)}
                      </td>
                      <td className="py-2 pl-2 text-right text-slate-600">
                        {formatCurrency(tx.balanceAfter, tx.currency)}
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {withdrawOpen && (
        <div className="va-modal fixed inset-0 z-[9999] flex items-end justify-center overflow-y-auto overscroll-contain bg-black/30 px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-4 sm:items-center sm:py-8">
          <div className="va-modal-panel w-full max-w-[420px] rounded-lg bg-white p-4 shadow min-h-[40vh]">
            <div className="flex justify-end">
              <RxCross1 size={25} onClick={() => setWithdrawOpen(false)} className="cursor-pointer" />
            </div>
            <h3 className="text-xl font-semibold text-center">Withdraw funds</h3>
            <p className="mt-2 text-sm text-slate-600 text-center">
              Funds go to your bank using the payout details from wallet setup. You don&apos;t need to sign in to Stripe.
            </p>
            <div className="mt-6 space-y-4 px-2">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Amount ({currency})
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmount(normalizeDecimalTyping(e.target.value))}
                  className={`${styles.input}`}
                  placeholder="0.00"
                />
                <p className="mt-1 text-xs text-slate-500">
                  Available to withdraw: {formatCurrency(withdrawableBalance, currency)}
                </p>
                {maxWithdraw > 0 && (
                  <button
                    type="button"
                    className="text-xs font-medium text-[#38513b] hover:underline"
                    onClick={() => setWithdrawAmount(String(maxWithdraw))}
                  >
                    Withdraw all ({formatCurrency(maxWithdraw, currency)})
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={handleWithdraw}
                disabled={isWithdrawing}
                className={`${styles.button} w-full text-white disabled:cursor-not-allowed disabled:opacity-60`}
              >
                {isWithdrawing ? "Processing withdrawal…" : "Confirm withdrawal"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Wallet;
